/**
 * Composant Card - Affiche une carte de Tarot
 */

import { useState, memo } from 'react';
import type { CardNumber } from '../types/game';
import { getCardImagePath, getCardBackImagePath, getCardName } from '../utils/cards';
import '../styles/card.css';

interface CardProps {
  /** Numéro de la carte (1-78) ou null pour le dos */
  card: CardNumber | null;
  
  /** Taille de la carte */
  size?: 'small' | 'medium' | 'large';

  /** Largeur explicite en px, prioritaire sur `size` (tapis de jeu) */
  width?: number;

  /** Hauteur explicite en px, prioritaire sur `size` (tapis de jeu) */
  height?: number;
  
  /** Carte sélectionnée (surbrillance) */
  selected?: boolean;
  
  /** Carte jouable (peut être cliquée) */
  playable?: boolean;
  
  /** Carte désactivée (grisée) */
  disabled?: boolean;
  
  /** Handler de clic */
  onClick?: (card: CardNumber) => void;
  
  /** Rotation en degrés (pour affichage en éventail) */
  rotation?: number;
  
  /** Décalage vertical en pixels */
  offsetY?: number;
  
  /** Z-index pour l'empilement */
  zIndex?: number;
  
  /** Animation d'entrée */
  animate?: boolean;
  
  /** Délai d'animation en ms */
  animationDelay?: number;
  
  /** Classe CSS additionnelle */
  className?: string;
}

/**
 * Composant mémorisé pour éviter les re-renders inutiles
 */
const Card = memo(function Card({
  card,
  size = 'medium',
  width,
  height,
  selected = false,
  playable = false,
  disabled = false,
  onClick,
  rotation = 0,
  offsetY = 0,
  zIndex = 0,
  animate = false,
  animationDelay = 0,
  className = '',
}: CardProps) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  
  // Image source
  const imageSrc = card ? getCardImagePath(card) : getCardBackImagePath();
  const altText = card ? getCardName(card) : 'Dos de carte';
  
  // Classes CSS
  const cardClasses = [
    'card',
    `card-${size}`,
    selected && 'card-selected',
    playable && 'card-playable',
    disabled && 'card-disabled',
    animate && 'card-animate',
    imageLoaded && 'card-loaded',
    className,
  ].filter(Boolean).join(' ');
  
  // Styles inline pour rotation/position
  const cardStyle: React.CSSProperties = {
    transform: `rotate(${rotation}deg) translateY(${offsetY}px)`,
    zIndex,
    animationDelay: animate ? `${animationDelay}ms` : undefined,
    width: width !== undefined ? `${width}px` : undefined,
    height: height !== undefined ? `${height}px` : undefined,
  };
  
  // Handler de clic
  const handleClick = () => {
    if (card && onClick && !disabled) {
      onClick(card);
    }
  };
  
  return (
    <div 
      className={cardClasses}
      style={cardStyle}
      onClick={handleClick}
      title={altText}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick && !disabled ? 0 : undefined}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleClick();
        }
      }}
    >
      {!imageError ? (
        <img
          src={imageSrc}
          alt={altText}
          className="card-image"
          onLoad={() => setImageLoaded(true)}
          onError={() => setImageError(true)}
          draggable={false}
        />
      ) : (
        <div className="card-fallback">
          <span className="card-fallback-text">
            {card ? `#${card}` : '?'}
          </span>
        </div>
      )}
      
      {/* Overlay pour les effets */}
      <div className="card-overlay" />
    </div>
  );
});

export default Card;

// ============================================
// Composants auxiliaires
// ============================================

interface CardPlaceholderProps {
  size?: 'small' | 'medium' | 'large';
  label?: string;
  className?: string;
}

/**
 * Placeholder pour une carte (emplacement vide)
 */
export function CardPlaceholder({ 
  size = 'medium', 
  label,
  className = '' 
}: CardPlaceholderProps) {
  return (
    <div className={`card-placeholder card-${size} ${className}`}>
      {label && <span className="placeholder-label">{label}</span>}
    </div>
  );
}

interface CardStackProps {
  cards: CardNumber[];
  size?: 'small' | 'medium' | 'large';
  maxVisible?: number;
  onClick?: () => void;
  className?: string;
}

/**
 * Pile de cartes empilées
 */
export function CardStack({
  cards,
  size = 'medium',
  maxVisible = 3,
  onClick,
  className = '',
}: CardStackProps) {
  const visibleCards = cards.slice(0, maxVisible);
  
  return (
    <div 
      className={`card-stack ${className}`}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
    >
      {visibleCards.map((card, index) => (
        <Card
          key={`${card}-${index}`}
          card={card}
          size={size}
          zIndex={index}
          offsetY={-index * 2}
        />
      ))}
      {cards.length > maxVisible && (
        <span className="stack-count">+{cards.length - maxVisible}</span>
      )}
    </div>
  );
}
